import {
    type Dispatch,
    type SetStateAction,
    useEffect,
    useState,
} from "react";
import { type SelectOptionType } from "@onshopfront/components";
import { type Options, subscribeToOption, unsubscribeFromOption } from "../Platform/Options/Options";
import { CacheRequest } from "../Platform/Request";

export enum MDBTypes {
    Category = "category",
    Brand = "brand",
    Family = "family",
    Tag = "tag",
    Supplier = "supplier",
    PromotionCategories = "promotioncategories",
    TaxRates = "taxrates",
}

export const MDB_TYPE_DETAILS: Record<MDBTypes, { url: string; }> = {
    brand: {
        url: "/mdb/brands",
    },
    category: {
        url: "/mdb/categories",
    },
    family: {
        url: "/mdb/families",
    },
    tag: {
        url: "/mdb/tags",
    },
    supplier: {
        url: "/mdb/suppliers",
    },
    promotioncategories: {
        url: "/mdb/promotion/categories",
    },
    taxrates: {
        url: "/mdb/taxes",
    },
};

export async function loadMDBRefs(type: MDBTypes): Promise<Array<SelectOptionType<number>>> {
    const response = await CacheRequest.get<Array<{
        name: string;
        id: number;
    }>>(MDB_TYPE_DETAILS[type].url);

    return response.data.map(option => ({
        label: option.name,
        value: option.id,
    }));
}

export type MDBSelectHook<IsClearable extends boolean = false> = [
    selected: SelectOptionType<number> | null,
    setSelected: Dispatch<SetStateAction<
        IsClearable extends true ?
            SelectOptionType<number> | null :
            SelectOptionType<number>
    >>,
    loading: boolean,
    options: Array<SelectOptionType<number>>,
];

/**
 * Hook for whether the Shopfront should be showing MDB stuff
 */
export function useMDBEnabled(): boolean {
    const [ enabled, setEnabled ] = useState(false);

    useEffect(() => {
        const listener = (_: "industry", industry: Options["industry"]) => {
            setEnabled(industry === "liquor");
        };

        const industry = subscribeToOption("industry", listener);

        setEnabled(industry === "liquor");

        return () => {
            unsubscribeFromOption("industry", listener);
        };
    }, []);

    return enabled;
}

/**
 * Hook for the logic of handling MDB Select
 * @param type
 * @param defaultValue
 */
export function useMDBSelect<IsClearable extends boolean = false>(
    type: MDBTypes,
    defaultValue: null | number | SelectOptionType<number> = null
): MDBSelectHook<IsClearable> {
    const enabled = useMDBEnabled();

    const [ selected, setSelected ] = useState<SelectOptionType<number> | null>(() => {
        // Ignore number, will be loaded with the MDB Refs
        if(typeof defaultValue === "number") {
            return null;
        }

        return defaultValue;
    });

    const [ loading, setLoading ] = useState(enabled);
    const [ options, setOptions ] = useState<Array<SelectOptionType<number>>>([]);

    useEffect(() => {
        if(!enabled) {
            setOptions([]);
            setLoading(false);

            return;
        }

        setLoading(true);

        loadMDBRefs(type)
            .then(options => {
                setOptions(options);
            })
            .catch(err => {
                console.error(`Error loading MDB refs for ${type}`, err);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [ type, enabled ]);

    useEffect(() => {
        if(typeof defaultValue !== "number") {
            return;
        }

        const found = options.find(option => option.value === defaultValue);

        if(!found) {
            return;
        }

        setSelected(found);
    }, [ options, defaultValue ]);

    return [
        selected,
        setSelected as MDBSelectHook<IsClearable>[1],
        loading,
        options,
    ];
}
